/**
 * Product Link Tooltip
 * Shows a product card tooltip when hovering over product links in article content
 */

class ProductLinkTooltip {
  constructor() {
    this.tooltip = null;
    this.currentLink = null;
    this.hoverTimeout = null;
    this.hideTimeout = null;
    this.productCache = new Map();
    this.init();
  }

  init() {
    this.createTooltip();
    this.attachEventListeners();
    this.attachWindowListeners();
  }

  createTooltip() {
    // Create tooltip container
    this.tooltip = document.createElement('div');
    this.tooltip.className = 'product-link-tooltip';
    this.tooltip.innerHTML = `
      <div class="product-link-tooltip__inner">
        <div class="product-link-tooltip__loading">
          <svg class="spinner" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <circle class="spinner-circle" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none"/>
          </svg>
        </div>
        <div class="product-link-tooltip__content"></div>
      </div>
    `;
    document.body.appendChild(this.tooltip);

    // Add mouse events to tooltip itself
    this.tooltip.addEventListener('mouseenter', () => {
      clearTimeout(this.hideTimeout);
    });

    this.tooltip.addEventListener('mouseleave', () => {
      this.hideTooltip();
    });
  }

  attachEventListeners() {
    // Use event delegation on article body
    const articleBody = document.querySelector('.article-body');
    if (!articleBody) return;

    articleBody.addEventListener('mouseover', (e) => {
      const link = e.target.closest('a[href*="/products/"]');
      if (link && this.isProductLink(link)) {
        this.handleLinkHover(link);
      }
    });

    articleBody.addEventListener('mouseout', (e) => {
      const link = e.target.closest('a[href*="/products/"]');
      if (link) {
        this.handleLinkLeave(link);
      }
    });
  }

  attachWindowListeners() {
    // Reposition tooltip on scroll
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      if (this.currentLink && this.tooltip.classList.contains('is-visible')) {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this.positionTooltip(this.currentLink);
        }, 10);
      }
    }, { passive: true });

    // Reposition tooltip on resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      if (this.currentLink && this.tooltip.classList.contains('is-visible')) {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.positionTooltip(this.currentLink);
        }, 100);
      }
    });
  }

  isProductLink(link) {
    const href = link.getAttribute('href');
    const hasText = link.innerText && link.innerText.trim().length > 0;
    // Check if it's a product link (contains /products/) and has text content
    return href && href.includes('/products/') && !href.includes('#') && hasText;
  }

  handleLinkHover(link) {
    clearTimeout(this.hideTimeout);
    
    // Add a small delay before showing tooltip
    this.hoverTimeout = setTimeout(() => {
      this.currentLink = link;
      this.showTooltip(link);
    }, 300);
  }

  handleLinkLeave(link) {
    clearTimeout(this.hoverTimeout);
    
    // Add a small delay before hiding to allow moving to tooltip
    this.hideTimeout = setTimeout(() => {
      if (this.currentLink === link) {
        this.hideTooltip();
      }
    }, 200);
  }

  async showTooltip(link) {
    const productHandle = this.extractProductHandle(link.getAttribute('href'));
    if (!productHandle) return;

    // Position tooltip near link
    this.positionTooltip(link);
    
    // Show loading state
    this.tooltip.classList.add('is-visible', 'is-loading');

    try {
      const productData = await this.fetchProductData(productHandle);
      if (productData && this.currentLink === link) {
        this.renderProductCard(productData);
        this.tooltip.classList.remove('is-loading');
      }
    } catch (error) {
      console.error('Error loading product data:', error);
      this.hideTooltip();
    }
  }

  hideTooltip() {
    this.tooltip.classList.remove('is-visible', 'is-loading');
    this.currentLink = null;
  }

  extractProductHandle(url) {
    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  async fetchProductData(handle) {
    // Check cache first
    if (this.productCache.has(handle)) {
      return this.productCache.get(handle);
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error('Product not found');
      
      const data = await response.json();
      this.productCache.set(handle, data);
      return data;
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  }

  renderProductCard(product) {
    const content = this.tooltip.querySelector('.product-link-tooltip__content');
    const variant = product.variants[0];
    const image = product.featured_image || (product.images && product.images[0]);
    
    // Format price
    const price = this.formatMoney(variant.price);
    const comparePrice = variant.compare_at_price > variant.price 
      ? this.formatMoney(variant.compare_at_price) 
      : null;

    // Check availability
    const available = product.available;
    const hasSingleVariant = product.variants.length === 1;
    const isPreOrder = product.tags && product.tags.includes('pre-order');

    content.innerHTML = `
      <div class="product-tooltip-card">
        <div 
        data-gtm-component="product-card"
        data-gtm-product="{{ product.title }}"
        {% comment %} data-gtm-product-sku="{% if product.variants.size > 1 %}{{ product.selected_or_first_available_variant.sku }}{% else %}{{ product.variants.first.sku }}{% endif %}" {% endcomment %}
        data-gtm-product-sku="{{ product.handle | escape }}"
        
        class="card product-card product-card--{{ settings.card_style }}{% if settings.card_border_thickness > 0 %} product-card--thickness{% endif %}{% unless featured_media %} no-media{% endunless %}{% if product.available and show_quick_add and mobile_quick_add %} mobile:with-quick-add{% endif %} grid leading-none relative overflow-hidden">

          <div class="product-card__media relative h-auto overflow-hidden">
          ${image ? `
            <div class="product-tooltip-card__image">
              <img src="${image}" alt="${product.title}" loading="lazy">
            </div>
          ` : ''}
          </div>
          <div class="product-card__content flex flex-col justify-start text-center">
            <div class="product-card__top w-full">
              <a
                data-gtm-element="product-card-group"
                class="caption reversed-link uppercase leading-none tracking-widest" href="#">
                
                 ${product.vendor}
              </a>
            </div>

            <div class="product-card__details flex flex-col lg:flex-row items-baseline gap-2 w-full">
              <p class="grow">
                <a
                  data-gtm-element="product-card-title"
                  class="product-card__title reversed-link text-base-xl font-medium leading-tight" href="{{ product_url }}">
                  ${product.title}
                </a>
              </p>

              <div class="price flex flex-wrap lg:flex-col lg:items-end gap-2 md:gap-1d5">
                <span
                  data-gtm-element="product-card-price"
                  class="price__regular text-base-lg font-medium{% if compare_at_price > price %} line-through{% endif %}">
         

                  ${comparePrice ? `<span class="product-tooltip-card__compare-price">${comparePrice}</span>` : ''}
              <span class="product-tooltip-card__final-price">${price}</span>
                </span>
              </div>

              ${this.renderQuickAddButton(product, hasSingleVariant, available, isPreOrder)}
            </div>
          </div>
          <div class="product-tooltip-card__details hidden">
            <h3 class="product-tooltip-card__title">${product.title}</h3>
            ${product.vendor ? `<p class="product-tooltip-card__vendor">${product.vendor}</p>` : ''}
            <div class="product-tooltip-card__price">
              ${comparePrice ? `<span class="product-tooltip-card__compare-price">${comparePrice}</span>` : ''}
              <span class="product-tooltip-card__final-price">${price}</span>
            </div>
            ${!available ? '<p class="product-tooltip-card__sold-out">Sold Out</p>' : ''}
          </div>
        </div>
      </div>
    `;

    // Attach event listener to quick add form if present
    const form = content.querySelector('form[is="product-form"]');
    if (form) {
      form.addEventListener('submit', (e) => this.handleQuickAdd(e, product));
    }
  }

  renderQuickAddButton(product, hasSingleVariant, available, isPreOrder) {
    if (!available) {
      return `
        <div class="quick-add flex justify-center w-full mt-4">
          <button type="button" class="button button--secondary button--md" disabled>
            Sold Out
          </button>
        </div>
      `;
    }

    if (hasSingleVariant) {
      const buttonLabel = isPreOrder ? 'Pre-Order' : 'Add to Cart';
      const variantId = product.variants[0].id;
      return `
        <div class="quick-add flex justify-center w-full mt-4">
          <form method="post" action="/cart/add" is="product-form" data-product-id="${product.id}">
            <input type="hidden" name="id" value="${variantId}" />
            <button type="submit" class="button button--primary button--md icon-with-text" data-variant-id="${variantId}">
              <span class="btn-fill"></span>
              <span class="btn-text">
                <!-- <svg class="icon icon--sm" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2 2h1.5l2.5 8h6l2-6H5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="7" cy="13" r="1" fill="currentColor"/>
                  <circle cx="12" cy="13" r="1" fill="currentColor"/>
                </svg> -->
                <span>${buttonLabel}</span>
              </span>
            </button>
          </form>
        </div>
      `;
    } else {
      return `
        <div class="quick-add flex justify-center w-full mt-4">
          <a href="${product.url}" class="button button--primary button--md icon-with-text">
            <span class="btn-fill"></span>
            <span class="btn-text">
              <svg class="icon icon--sm" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 2h1.5l2.5 8h6l2-6H5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="7" cy="13" r="1" fill="currentColor"/>
                <circle cx="12" cy="13" r="1" fill="currentColor"/>
              </svg>
              <span>Choose Options</span>
            </span>
          </a>
        </div>
      `;
    }
  }

  async handleQuickAdd(e, product) {
    e.preventDefault();
    const form = e.target;
    const button = form.querySelector('button[type="submit"]');
    const buttonText = button.querySelector('.btn-text span:last-child');
    const originalText = buttonText.textContent;

    // Disable button and show loading state
    button.setAttribute('disabled', 'true');
    button.setAttribute('aria-busy', 'true');
    buttonText.textContent = originalText;

    try {
      const formData = new FormData(form);
      const config = {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        body: formData
      };

      const response = await fetch('/cart/add.js', config);
      const data = await response.json();

      if (data.status === 422 || data.status === 'error') {
        throw new Error(data.description || 'Unable to add to cart');
      }

      // Success - update cart
      // buttonText.textContent = 'Added!';
      
      // Trigger cart update event
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      // Dispatch events for cart updates
      if (typeof theme !== 'undefined' && theme.pubsub) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartData });
      }
      
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: { product: data }
      }));

      // Hide tooltip after short delay
      setTimeout(() => {
        this.hideTooltip();
      }, 1000);

    } catch (error) {
      console.error('Error adding to cart:', error);
      // buttonText.textContent = 'Error';
      
      // Reset button after delay
      setTimeout(() => {
        buttonText.textContent = originalText;
        button.removeAttribute('disabled');
        button.removeAttribute('aria-busy');
      }, 2000);
    }
  }

  positionTooltip(link) {
    const rect = link.getBoundingClientRect();
    const padding = 16;
    const arrowOffset = 10; // Space for visual arrow/pointer
    
    // Set initial position to measure actual tooltip dimensions
    this.tooltip.style.visibility = 'hidden';
    this.tooltip.style.display = 'block';
    
    // Get actual tooltip dimensions after render
    const tooltipRect = this.tooltip.getBoundingClientRect();
    const tooltipWidth = tooltipRect.width || 320;
    const tooltipHeight = tooltipRect.height || 400;
    
    this.tooltip.style.visibility = '';
    this.tooltip.style.display = '';

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;

    let top, left;
    let placement = 'bottom'; // Track placement for styling

    // Calculate horizontal position (centered under link by default)
    left = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2);

    // Adjust horizontal position if it goes off screen
    if (left < padding) {
      left = padding;
    } else if (left + tooltipWidth > viewportWidth - padding) {
      left = viewportWidth - tooltipWidth - padding;
    }

    // Calculate vertical position
    // Try below first
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow >= tooltipHeight + padding + arrowOffset) {
      // Enough space below
      top = rect.bottom + scrollY + arrowOffset;
      placement = 'bottom';
    } else if (spaceAbove >= tooltipHeight + padding + arrowOffset) {
      // Not enough space below, try above
      top = rect.top + scrollY - tooltipHeight - arrowOffset;
      placement = 'top';
    } else {
      // Not enough space above or below, use side with more space
      if (spaceBelow > spaceAbove) {
        // Position at bottom but ensure it stays in viewport
        top = rect.bottom + scrollY + arrowOffset;
        // Adjust if it goes below viewport
        const maxTop = scrollY + viewportHeight - tooltipHeight - padding;
        if (top > maxTop) {
          top = maxTop;
        }
        placement = 'bottom';
      } else {
        // Position at top but ensure it stays in viewport
        top = rect.top + scrollY - tooltipHeight - arrowOffset;
        // Adjust if it goes above viewport
        const minTop = scrollY + padding;
        if (top < minTop) {
          top = minTop;
        }
        placement = 'top';
      }
    }

    // Ensure tooltip doesn't go above page top
    if (top < scrollY + padding) {
      top = scrollY + padding;
    }

    // Ensure tooltip doesn't go below visible area
    const maxTop = scrollY + viewportHeight - tooltipHeight - padding;
    if (top > maxTop) {
      top = Math.max(scrollY + padding, maxTop);
    }

    // Apply final position
    this.tooltip.style.top = `${top}px`;
    this.tooltip.style.left = `${left}px`;
    this.tooltip.setAttribute('data-placement', placement);
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    // Use Shopify's money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    return `$${amount}`;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductLinkTooltip();
  });
} else {
  new ProductLinkTooltip();
}
